import { useQuery } from "@tanstack/react-query";
import Cookies from "js-cookie";

import { getCurrentEmployeeCheckinsQueryOptions } from "@/config/queries/emp-query-options";
import { useMemo } from "react";
import { format } from "date-fns";
import { DataTable } from "./data-table";
import { columns } from "./columns";

function areSameDay(checkin: string | Date, checkout: string | Date): boolean {
  const checkinDate = new Date(checkin);
  const checkoutDate = new Date(checkout);

  return (
    checkinDate.getFullYear() === checkoutDate.getFullYear() &&
    checkinDate.getMonth() === checkoutDate.getMonth() &&
    checkinDate.getDate() === checkoutDate.getDate()
  );
}

export const CheckinTableForEmployee = () => {
  const key = Cookies.get("key") as string;

  const { data, isPending } = useQuery(
    getCurrentEmployeeCheckinsQueryOptions(key)
  );

  const testData = useMemo(() => {
    if (isPending) {
      return [];
    }

    if (data === undefined || data?.checkins === undefined) {
      return [];
    }

    const inLogs = data?.checkins?.filter((entry) => entry?.mode === "IN");
    const outLogs = data?.checkins?.filter((entry) => entry?.mode === "OUT");

    const pairedLogs: {
      inLog: {
        mode: string;
        createdAt: string | Date;
      };
      outLog: {
        mode: string;
        createdAt: string | Date;
      } | null;
    }[] = [];

    for (const inLog of inLogs) {
      const correspondingOutLog = outLogs.find(
        (outLog) =>
          areSameDay(inLog?.createdAt, outLog?.createdAt) &&
          !pairedLogs.some((pair) => pair.outLog === outLog) // Check if this outLog has been paired already
      );

      if (correspondingOutLog) {
        pairedLogs.push({ inLog, outLog: correspondingOutLog });
      } else {
        pairedLogs.push({
          inLog,
          outLog: null,
        });
      }
    }

    let result2 = [];

    for (const pair of pairedLogs) {
      if (pair.outLog !== null) {
        const diffInMs = Math.abs(
          new Date(pair?.inLog?.createdAt).getTime() -
            new Date(pair?.outLog?.createdAt).getTime()
        );
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
        const diffInMinutes = Math.floor(
          (diffInMs % (1000 * 60 * 60)) / (1000 * 60)
        );

        if (diffInHours > 8) {
          const formattedDuration = `${diffInHours} hours and ${diffInMinutes} minutes`;

          result2.push({
            rawTime: new Date(pair?.inLog?.createdAt),
            checkinTime: format(
              new Date(pair?.inLog?.createdAt as Date),
              "PPpp"
            ),
            checkoutTime: format(
              new Date(pair?.outLog?.createdAt as Date),
              "PPpp"
            ),
            totalWorkedHours: formattedDuration,
            status: "Full Day",
          });
        } else {
          const formattedDuration = `${diffInHours} hours and ${diffInMinutes} minutes`;

          result2.push({
            rawTime: new Date(pair?.inLog?.createdAt),
            checkinTime: format(
              new Date(pair?.inLog?.createdAt as Date),
              "PPpp"
            ),
            checkoutTime: format(
              new Date(pair?.outLog?.createdAt as Date),
              "PPpp"
            ),
            totalWorkedHours: formattedDuration,
            status: "Not Counting",
          });
        }
      } else {
        const diffInMs = Math.abs(
          new Date(pair?.inLog?.createdAt).getTime() - new Date().getTime()
        );

        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
        const diffInMinutes = Math.floor(
          (diffInMs % (1000 * 60 * 60)) / (1000 * 60)
        );

        const isFullDay = diffInHours >= 8;

        const formattedDuration = isFullDay
          ? `More than 8 hours or forgot to checkout!`
          : `${diffInHours} hours and ${diffInMinutes} minutes`;

        result2.push({
          rawTime: new Date(pair?.inLog?.createdAt),
          checkinTime: format(new Date(pair?.inLog?.createdAt as Date), "PPpp"),
          checkoutTime: "",
          totalWorkedHours: formattedDuration,
          status: isFullDay ? "Full Day" : "Still Working",
        });
      }
    }

    return result2.sort((a, b) => b.rawTime.getTime() - a.rawTime.getTime());
  }, [data, isPending]);

  // const checkinsData = useMemo(() => {
  //   if (isPending) {
  //     return [];
  //   }

  //   if (data === undefined || data?.checkins === undefined) {
  //     return [];
  //   }

  //   const result = data?.checkins?.map((checkin) => {
  //     return {
  //       id: checkin?._id,
  //       mode: checkin?.mode === "IN" ? "Check in" : "Check out",
  //       time: format(new Date(checkin?.createdAt as Date), "PPpp"),
  //       createdAtIST: checkin?.createdAtIST,
  //     };
  //   });

  //   return result;
  // }, [isPending, data]);

  return (
    <>
      <DataTable data={testData} columns={columns} />
    </>
  );
};
